import './ApiDocTabs.scss';
import '../AppTabs/AppTabs.scss';
import { apiSublevels } from '../../pages/Api/navInfo';
import {
  Box,
  createTheme,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  ThemeProvider,
  useMediaQuery
} from '@mui/material';
import {
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import AppAccordion from '../AppAccordion/AppAccordion';
import AppWarningBanner from '../../components/AppWarningBanner/AppWarningBanner';
import CodeSnippet from '../CodeSnippet/CodeSnippet';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import parse, {
  domToReact,
  HTMLReactParserOptions,
  Element,
  DOMNode
} from 'html-react-parser';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';

const theme = createTheme({
  palette: {
    secondary: {
      main: '#005078'
    }
  }
});

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      aria-labelledby={`tab-${index}`}
      hidden={value !== index}
      id={`tabpanel-${index}`}
      role="tabpanel"
      {...other}
    >
      {value === index && (
        <Box className="api-doc-panel" paddingTop={2}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (key: string) => {
  return {
    id: `tab-${key}`,
    'aria-controls': `tabpanel-${key}`
  };
};

interface Props {
  data: any;
  tabs: { [key: string]: string };
  selectedTab?: string;
  setCurrLinks?: any;
}

const ApiDocTabs = ({ data, tabs, selectedTab, setCurrLinks }: Props) => {

  //File Rename

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [resetKey, setResetKey] = useState(0);

  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  const convertTableToMobileView = (htmlString: string) => {
    if (!isMobile) return htmlString;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    const headers = Array.from(doc.querySelectorAll('thead th')).map((th) =>
      th.textContent?.trim()
    );
    const rows = doc.querySelectorAll('tbody tr');

    let mobileViewHtml = Array.from(rows)
      .map((row) => {
        const cells = row.querySelectorAll('td');
        let rowHtml = '<div class="mobile-row">';

        cells.forEach((cell, index) => {
          rowHtml += `
          <div class="mobile-subrow">
            <p class="mobile-subrow-header">${headers[index]}</p>
            <p>${cell.textContent?.trim()}</p>
          </div>
        `;
        });

        rowHtml += '</div>';
        return rowHtml;
      })
      .join('');

    mobileViewHtml = `<div class="mobile-table-view">${mobileViewHtml}</div>`;

    const tableElement = doc.querySelector('table');
    if (tableElement) {
      tableElement.outerHTML = mobileViewHtml;
    }

    return doc.body.innerHTML;
  };

  const handleChange = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setCurrLinks(prevState => {
      const newState = [...prevState];
      newState[2] = apiSublevels[newValue];
      return newState;
    });
  };

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (index: number) => {
    setTabValue(index);
    setCurrLinks(prevState => {
      const newState = [...prevState];
      newState[2] = apiSublevels[index];
      return newState;
    });
    setAnchorEl(null);
  };

  const renderMobileTabs = () => {
    return (
      <div id="mobNavContainer">
        <List
          aria-label={t('api_documentation.settings')}
          className="mobTabsMenu"
          component="nav"
          sx={{ bgcolor: 'background.paper', margin: '0 auto' }}
        >
          <ListItem
            aria-controls="lock-menu"
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="listbox"
            aria-label={`${Object.values(tabs)[tabValue]}`}
            id="lock-button"
            onClick={handleClickListItem}
          >
            <ListItemText primary={Object.values(tabs)[tabValue]} />
            <KeyboardArrowDownIcon className="mobTabMenuSelectArrow" />
          </ListItem>
        </List>
        <Menu
          anchorEl={anchorEl}
          id="lock-menu"
          MenuListProps={{
            'aria-labelledby': 'lock-button',
            role: 'listbox'
          }}
          onClose={handleClose}
          open={open}
        >
          {Object.entries(tabs).map(([key, value], index: number) => (
            <MenuItem
              aria-label={`${Object.values(tabs)[tabValue]}-item`}
              disabled={index === tabValue}
              key={key}
              onClick={() => handleMenuItemClick(index)}
              role="button"
              selected={index === tabValue}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  const renderTabPanelContent = (index: number) => {
    const tabDataMap: { [key: number]: any } = {};
    let tabDataMapIndex = 0;

    if (tabs?.functional_capabilities) {
      tabDataMap[tabDataMapIndex] = data?.functional_capabilities?.[0];
      tabDataMapIndex++;
    }
    if (tabs?.request) {
      tabDataMap[tabDataMapIndex] = data?.request?.[0];
      tabDataMapIndex++;
    }
    if (tabs?.response) {
      tabDataMap[tabDataMapIndex] = data?.response?.[0];
      tabDataMapIndex++;
    }
    if (tabs?.use_cases) {
      tabDataMap[tabDataMapIndex] = data?.use_cases?.[0];
      tabDataMapIndex++;
    }
    if (tabs?.error_codes) {
      tabDataMap[tabDataMapIndex] = data?.error_codes?.[0];
      tabDataMapIndex++;
    }

    const tabData = tabDataMap[tabValue];

    return (
      <TabPanel key={`tabpanel-${index}`} index={index} value={tabValue}>
        <div className="mt-42">
          {tabData?.introduction && (
            <div
              dangerouslySetInnerHTML={{ __html: tabData.introduction }}
            />
          )}
          {tabData?.accordions?.map((accordion: any, index: number) => (
            <AppAccordion
              key={`accordion-${index}-${resetKey}`}
              details={convertTableToMobileView(transformPreTag(
                transformWarningBannerDiv(accordion.details)
              ))}
              summary={accordion.summary}
            />
          ))}
        </div>
      </TabPanel>
    );
  };

  const transformWarningBannerDiv = (htmlString: string) => {
    const transformedHtmlString = htmlString?.replace(
      /<div class="\\&quot;warning-banner\\&quot;">([\s\S]*?)<\/div>/g,
      (_, p1) => `<warning-banner>${p1}</warning-banner>`
    );

    const options: HTMLReactParserOptions = {
      replace: (node) => {
        if (
          node instanceof Element &&
          node.tagName.toLowerCase() === 'warning-banner'
        ) {
          const content = domToReact(node.children as any[]); // Get the content inside the custom <warning-banner>
          return (
            <AppWarningBanner
              htmlContent={ReactDOMServer.renderToStaticMarkup(
                content as ReactElement
              )}
            />
          );
        }
      }
    };

    const reactElement = parse(transformedHtmlString, options);

    return ReactDOMServer.renderToStaticMarkup(
      reactElement as ReactElement
    );
  };

  const transformPreTag = (htmlString: string) => {
    const transformedHtmlString = htmlString.replace(
      /<pre>([\s\S]*?)<\/pre>/g,
      (_, p1) => `<codesnippet>${p1}</codesnippet>`
    );

    const options: HTMLReactParserOptions = {
      replace: (node) => {
        if (
          node instanceof Element &&
          node.tagName.toLowerCase() === 'codesnippet'
        ) {
          const content = domToReact(node.children as DOMNode[]);
          return <CodeSnippet>{content}</CodeSnippet>;
        }
      }
    };

    const reactElement = parse(transformedHtmlString, options);

    return ReactDOMServer.renderToStaticMarkup(
      reactElement as React.ReactElement
    );
  };

  useEffect(() => {
    if (!data) return;
    if (data?.api_name === 'Scenarios') {
      setTabValue(0);
    } else {
      setTabValue(apiSublevels.findIndex((tab) => tab === selectedTab));
    }
    setResetKey(prevKey => prevKey + 1);
  }, [selectedTab, data]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="api-doc-tabs"
        mt="-10px"
        pb="16px"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {isMobile ? (
          renderMobileTabs()
        ) : (
          <Tabs
            aria-label="api doc tabs"
            indicatorColor="secondary"
            onChange={handleChange}
            textColor="secondary"
            value={tabValue}
          >
            {Object.entries(tabs).map(([key, value]) => (
              <Tab
                key={`tab-${key}`}
                {...a11yProps(key)}
                className="tab-label"
                label={value as string}
              />
            ))}
          </Tabs>
        )}
      </Box>
      {Object.keys(tabs).map((_, index) => renderTabPanelContent(index))}
    </ThemeProvider>
  );
};

export default ApiDocTabs;
